import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import en from './locales/en.json';

export const LANGS = ['en', 'ar', 'he'] as const;
export type Lang = (typeof LANGS)[number];
export const RTL_LANGS: ReadonlySet<Lang> = new Set<Lang>(['ar', 'he']);
export const LANG_LABELS: Record<Lang, string> = { en: 'English', ar: 'العربية', he: 'עברית' };

/** Number/currency locale per UI language (Arabic keeps Latin digits, common in the region). */
export const NUMBER_LOCALES: Record<Lang, string> = { en: 'en-IL', ar: 'ar-u-nu-latn', he: 'he-IL' };

const STORAGE_KEY = 'pp_lang';

export const isLang = (v: unknown): v is Lang => typeof v === 'string' && (LANGS as readonly string[]).includes(v);

void i18n.use(initReactI18next).init({
  resources: { en: { translation: en } },
  lng: 'en',
  fallbackLng: 'en',
  interpolation: { escapeValue: false },
  returnNull: false,
  react: { useSuspense: false },
});

/** Locale bundles other than English are code-split and loaded on demand. */
const loaders: Record<Exclude<Lang, 'en'>, () => Promise<{ default: Record<string, unknown> }>> = {
  ar: () => import('./locales/ar.json'),
  he: () => import('./locales/he.json'),
};

export async function ensureLocale(lang: Lang): Promise<void> {
  if (lang === 'en' || i18n.hasResourceBundle(lang, 'translation')) return;
  const mod = await loaders[lang]();
  i18n.addResourceBundle(lang, 'translation', mod.default, true, true);
}

/** Stored choice → browser languages → configured default → English. */
export function detectLanguage(): Lang {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (isLang(stored)) return stored;
  } catch {
    /* storage unavailable */
  }
  for (const l of navigator.languages ?? [navigator.language]) {
    const code = l?.slice(0, 2).toLowerCase();
    if (code === 'iw') return 'he';
    if (isLang(code)) return code;
  }
  const fallback = import.meta.env.VITE_DEFAULT_LANGUAGE;
  return isLang(fallback) ? fallback : 'en';
}

/** Apply a language to i18next and the document (lang + dir for full RTL support). */
export function applyLanguage(lang: Lang): void {
  if (i18n.language !== lang) void i18n.changeLanguage(lang);
  const root = document.documentElement;
  root.lang = lang;
  root.dir = RTL_LANGS.has(lang) ? 'rtl' : 'ltr';
  try {
    localStorage.setItem(STORAGE_KEY, lang);
  } catch {
    /* storage unavailable */
  }
}

export default i18n;
